import React, { useState, ReactElement, useEffect } from 'react';
import Sidebar from './Sidebar';
import { UserRole } from '../types/roles';
import { chatService } from '../services/chatService';
import { useThemeStore } from '../stores/themeStore';

// Define the interface here to avoid import issues
interface ChatLayoutProps {
  userRole: UserRole;
  selectedSubject: string | null;
  onSubjectSelect: (subjectId: string | null) => void;
  selectedThreadId: string | null;
  onThreadSelect: (threadId: string) => void;
  selectedProjectId: string | null;
  onProjectSelect: (projectId: string | null) => void;
}

interface LayoutProps {
  userRole: UserRole;
  children: ReactElement<ChatLayoutProps>;
}

const Layout: React.FC<LayoutProps> = ({ userRole, children }) => {
  const setIsAdmin = useThemeStore((state) => state.setIsAdmin);

  // Mark as non-admin user
  useEffect(() => {
    setIsAdmin(false);
  }, [setIsAdmin]);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  // Profile open state - setter unused as profile is managed in ChatLayout
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isProfileOpen, _setIsProfileOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [deletedThreadIds, setDeletedThreadIds] = useState<Set<string>>(new Set());
  const [deletedProjectIds, setDeletedProjectIds] = useState<Set<string>>(new Set());

  const handleSubjectSelect = (subjectId: string | null) => {
    console.log(`Layout: handleSubjectSelect called with ${subjectId}. Current selectedSubject: ${selectedSubject}, current selectedThreadId: ${selectedThreadId}`);
    setSelectedSubject(subjectId);
    setSelectedThreadId(null); 
    console.log(`Layout: After setSelectedSubject, selectedSubject is now ${subjectId}, selectedThreadId is null`);
  };

  const handleThreadSelect = (threadId: string) => {
    console.log(`Layout: handleThreadSelect called with ${threadId}. Current selectedThreadId: ${selectedThreadId}`);
    setSelectedThreadId(threadId || null);
    // Switching to a chat thread exits any project view.
    setSelectedProjectId(null);
    console.log(`Layout: After setSelectedThreadId, selectedThreadId is now ${threadId}`);
  };

  const handleProjectSelect = (projectId: string | null) => {
    setSelectedProjectId(projectId);
    // Switching to project view clears chat thread selection.
    setSelectedThreadId(null);
  };

  const handleThreadDelete = async (threadId: string) => {
    try {
      // Optimistically update UI
      setDeletedThreadIds(prev => new Set([...Array.from(prev), threadId]));
      
      // If the deleted thread was selected, clear the selection
      if (selectedThreadId === threadId) {
        setSelectedThreadId(null);
      }

      // Make the API call to delete the thread
      await chatService.deleteChat(threadId);
      
    } catch (error) {
      // Revert optimistic update on error
      setDeletedThreadIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(threadId);
        return newSet;
      });
      console.error('Failed to delete thread:', error);
    }
  };

  const handleProjectDelete = async (projectId: string) => {
    try {
      // Optimistically update UI
      setDeletedProjectIds(prev => new Set([...Array.from(prev), projectId]));
      
      // If the deleted project was selected, clear the selection
      if (selectedProjectId === projectId) {
        setSelectedProjectId(null);
      }

      // Make the API call to delete the project
      const { projectService } = await import('../services/projectService');
      await projectService.delete(projectId);
      
    } catch (error) {
      // Revert optimistic update on error
      setDeletedProjectIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(projectId);
        return newSet;
      });
      console.error('Failed to delete project:', error);
    }
  };

  const childrenWithProps = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement<ChatLayoutProps>(child, {
        ...child.props,
        userRole,
        selectedSubject,
        onSubjectSelect: handleSubjectSelect,
        selectedThreadId,
        onThreadSelect: handleThreadSelect,
        selectedProjectId,
        onProjectSelect: handleProjectSelect
      });
    }
    return child;
  });

  return (
    <div className="flex h-screen min-h-0 overflow-hidden bg-[var(--bg-primary)] text-[var(--text-primary)]">
      <Sidebar 
        isOpen={isSidebarOpen} 
        setIsOpen={setIsSidebarOpen}
        selectedSubject={selectedSubject}
        onSubjectSelect={handleSubjectSelect}
        onThreadSelect={handleThreadSelect}
        selectedThreadId={selectedThreadId}
        selectedProjectId={selectedProjectId}
        onProjectSelect={handleProjectSelect}
        onProjectDelete={handleProjectDelete}
        onThreadDelete={handleThreadDelete}
        deletedThreadIds={deletedThreadIds}
        deletedProjectIds={deletedProjectIds}
        subjects={[]}
        isProfileOpen={isProfileOpen}
      />
      {childrenWithProps}
    </div>
  );
};

export default Layout;